import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { InvoiceBuyerInput } from '@/lib/api/sales-order-api'
import type {
    EInvoice,
    EInvoiceDraftInput,
    EInvoiceDraftReset,
} from '@/lib/api/einvoice-api'
import {
    invalidateEInvoiceDocument,
    storeEInvoiceObservation,
} from '@/hooks/use-einvoice-observation'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { einvoiceApi } from '@/lib/api/einvoice-api'
import { getEInvoiceFailureMessage } from '@/lib/einvoice-failure-message'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'
import { einvoiceKeys } from '@/hooks/use-einvoice-query'
import { ApiError } from '@/lib/api'
import { invalidateRejectedEInvoiceCredential } from '@/hooks/use-einvoice-credential'

function updateDetail(
    queryClient: ReturnType<typeof useQueryClient>,
    orderId: string,
    data?: EInvoice,
    preserveObservation = false,
) {
    if (!preserveObservation) invalidateEInvoiceDocument(queryClient, orderId)
    if (data) queryClient.setQueryData(einvoiceKeys.detail(orderId), data)
    if (data?.observation)
        storeEInvoiceObservation(queryClient, orderId, data.observation)
    void queryClient.invalidateQueries({
        queryKey: einvoiceKeys.detail(orderId),
    })
}

/** "2026-09-18" -> "18/09/2026". Chuỗi ngày từ server đã là ngày lịch VN nên
 *  chỉ đảo thứ tự, không đưa qua Date để tránh lệch múi giờ của browser. */
function formatIsoDateVN(date: string) {
    const parts = date.split('-')
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : date
}

function showError(
    queryClient: ReturnType<typeof useQueryClient>,
    error: unknown,
    fallback: string,
) {
    invalidateRejectedEInvoiceCredential(queryClient, error)
    if (
        error instanceof ApiError &&
        error.code === 'EINVOICE_CREDENTIAL_MISSING'
    ) {
        toast.error(m.einvoice_credential_missing_title(), {
            description: m.einvoice_credential_missing_desc(),
        })
        return
    }
    if (
        error instanceof ApiError &&
        error.code === 'EINVOICE_CREDENTIAL_INVALID'
    ) {
        toast.error(m.einvoice_credential_blocked_title(), {
            description: m.einvoice_credential_invalid_toast_desc(),
        })
        return
    }
    const payload = formatApiError(error, fallback)
    toast.error(payload.title, { description: payload.description })
}

export function usePushEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (orderId: string) =>
            (await einvoiceApi.push(orderId)).data,
        onSuccess: (data, orderId) => {
            updateDetail(queryClient, orderId, data)
            if (data?.status === 'failed') {
                toast.error(
                    getEInvoiceFailureMessage(data) ??
                        m.einvoice_retry_failed(),
                )
                return
            }
            toast.success(m.einvoice_retry_sent())
        },
        onError: (error) =>
            showError(queryClient, error, m.einvoice_retry_failed()),
    })
}

export function useWithdrawEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (orderId: string) =>
            (await einvoiceApi.withdraw(orderId)).data,
        onSuccess: (data, orderId) => {
            updateDetail(queryClient, orderId, data)
            toast.success(m.einvoice_withdrawn())
        },
        onError: (error) =>
            showError(queryClient, error, m.einvoice_withdraw_failed()),
    })
}

export function useRefreshEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (orderId: string) =>
            (await einvoiceApi.refresh(orderId)).data,
        onSuccess: (data, orderId) => {
            // MISA is still signing: keep the live "issuing" card, which polling
            // replaces once reconcile records the result. A stored observation
            // would outlive that change.
            if (data?.observation?.publication === 'processing') {
                updateDetail(
                    queryClient,
                    orderId,
                    { ...data, observation: null },
                    true,
                )
                toast.info(m.einvoice_refresh_still_publishing())
                return
            }
            updateDetail(queryClient, orderId, data, true)
            toast.success(m.einvoice_refreshed())
        },
        onError: (error) =>
            showError(queryClient, error, m.einvoice_refresh_failed()),
    })
}

export function useSyncEInvoiceTemplates() {
    const queryClient = useQueryClient()
    return useMutation({
        onError: (error) =>
            invalidateRejectedEInvoiceCredential(queryClient, error),
        mutationFn: async () => (await einvoiceApi.syncTemplates()).data ?? [],
    })
}

export function useRefreshEInvoiceDraft() {
    const queryClient = useQueryClient()
    return useMutation({
        onError: (error) =>
            invalidateRejectedEInvoiceCredential(queryClient, error),
        mutationFn: async ({
            orderId,
            buyer,
            draft,
            reset,
        }: {
            orderId: string
            buyer?: InvoiceBuyerInput
            draft?: EInvoiceDraftInput
            reset?: EInvoiceDraftReset
        }) =>
            (await einvoiceApi.refreshDraft(orderId, buyer, draft, reset)).data,
        onSuccess: (data, { orderId }) => {
            updateDetail(queryClient, orderId, data)
            return Promise.all([
                queryClient.invalidateQueries({
                    queryKey: orderKeys.detail(orderId),
                }),
                queryClient.invalidateQueries({
                    queryKey: einvoiceKeys.editor(orderId),
                }),
            ])
        },
    })
}

export function usePublishEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({
            orderId,
            previewToken,
            notIssuedByHand,
        }: {
            orderId: string
            previewToken: string
            notIssuedByHand?: boolean
        }) =>
            (await einvoiceApi.publish(orderId, previewToken, notIssuedByHand))
                .data,
        onSuccess: (data, { orderId }) => {
            updateDetail(queryClient, orderId, data)
            // Đơn vừa phát hành rời nhóm "Chờ xuất hóa đơn": danh sách đơn và
            // dòng nhắc trên màn Đơn hàng phải đếm lại ngay (M-161).
            void queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
            if (data?.invoice_date_adjusted_to)
                toast.success(
                    m.einvoice_invoice_date_adjusted({
                        date: formatIsoDateVN(data.invoice_date_adjusted_to),
                    }),
                )
        },
        onError: (error, { orderId }) => {
            invalidateRejectedEInvoiceCredential(queryClient, error)
            invalidateEInvoiceDocument(queryClient, orderId)
            void queryClient.invalidateQueries({
                queryKey: einvoiceKeys.detail(orderId),
            })
        },
        // Xác nhận phát hành tích "Xuất hóa đơn" cho đơn chưa tích, kể cả khi
        // MISA từ chối sau đó (M-176): tải lại đơn trong cả hai trường hợp.
        onSettled: (_data, _error, { orderId }) => {
            void queryClient.invalidateQueries({
                queryKey: orderKeys.detail(orderId),
            })
        },
    })
}

/** M-167: đơn "Bán ngay" có tích hóa đơn — một lệnh tạo nháp và phát hành.
 *  Không toast: popup bán hàng hiện kết quả tại chỗ. Cập nhật cache nằm ở
 *  options (không ở chỗ gọi `mutate`) để vẫn chạy khi thu ngân đóng popup
 *  trước khi MISA trả lời. */
export function usePublishQuickSaleEInvoice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (orderId: string) =>
            (await einvoiceApi.publishQuickSale(orderId)).data,
        onSuccess: (data, orderId) => {
            updateDetail(queryClient, orderId, data)
            void queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
        },
        onError: (error, orderId) => {
            invalidateRejectedEInvoiceCredential(queryClient, error)
            void queryClient.invalidateQueries({
                queryKey: einvoiceKeys.detail(orderId),
            })
        },
    })
}
